/**
 * Secrets-redaction helpers. Scrub sensitive data out of strings, URLs and
 * arbitrary objects *before* logs are persisted or shipped. See decisions-log.md.
 *
 * Design notes:
 *  - Dependency-free, pure TypeScript, SSR/browser safe (no Node-only APIs).
 *  - Best-effort and total: nothing here ever throws — on any internal failure
 *    we fall back to the safest available behaviour (e.g. returning the input
 *    untouched, or routing through `redactString`).
 *  - `redactString` matches a fixed set of secret shapes and replaces each hit
 *    with a `[redacted:<kind>]` token so the surrounding context is preserved.
 *  - `redactObject` deep-clones (never mutates the caller's object), redacts the
 *    *values* of sensitively-named keys wholesale and runs `redactString` over
 *    every remaining string leaf. Cyclic / non-serializable inputs are handled.
 */

/** Sensitive query-string parameters whose VALUES are stripped from URLs. */
const SENSITIVE_URL_PARAMS: readonly string[] = [
  "token",
  "pin",
  "key",
  "secret",
  "access_token",
  "sig",
  "jwt",
];

/**
 * Object-key names (case-insensitive, exact match) whose values are replaced
 * wholesale with `[redacted:key]`, regardless of the value's shape.
 */
const SENSITIVE_KEY_NAMES: readonly string[] = [
  "password",
  "pass",
  "token",
  "secret",
  "authorization",
  "cookie",
  "pin",
  "e2ee_privkey_wrapped",
  "e2ee_recovery_wrapped",
  "e2ee_kdf_params",
  "privkey",
  "private_key",
  "apikey",
  "api_key",
  "access_token",
  "refresh_token",
  "bearer",
];

const SENSITIVE_KEY_SET: ReadonlySet<string> = new Set(
  SENSITIVE_KEY_NAMES.map((k) => k.toLowerCase()),
);

// --- redactString patterns (order matters: most specific first) ---

/** JWT: three base64url segments separated by dots, starting with `eyJ`. */
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

/** `Bearer <token>` (Authorization header style). */
const BEARER_RE = /\bBearer\s+[A-Za-z0-9._~+/-]+=*/g;

/** Cookie header form `avatar_sess=<value>` (our session cookie). */
const SESS_COOKIE_RE = /avatar_sess=[^;\s]+/g;

/**
 * Query-style `key=value` for sensitive parameter names embedded inside an
 * arbitrary string (e.g. `?token=abc&pin=1234`). Value is everything up to the
 * next `&`, whitespace, `;`, `"` or `'`.
 */
const QUERY_PARAM_RE =
  /\b(token|pin|key|secret|access_token|refresh_token|sig)=([^&\s;"']+)/gi;

/**
 * Long base64 / base64url blob that looks like a key/token. We require a
 * generous length (>= 40) AND that the run contains at least one digit and one
 * letter (so prose words / long identifiers built from a single class are not
 * clobbered). Matched as a standalone token (surrounded by non-charset chars).
 */
const LONG_BLOB_RE = /[A-Za-z0-9+/_-]{40,}={0,2}/g;

/** True when a candidate blob is "mixed" enough to plausibly be a secret. */
function looksLikeKey(s: string): boolean {
  const core = s.replace(/=+$/, "");
  if (core.length < 40) return false;
  const hasDigit = /[0-9]/.test(core);
  const hasAlpha = /[A-Za-z]/.test(core);
  // Require both a digit and a letter, OR the base64-specific chars (+/_-).
  const hasSpecial = /[+/_-]/.test(core);
  return (hasDigit && hasAlpha) || (hasAlpha && hasSpecial && hasDigit);
}

/**
 * Replace secrets in a free-form string with `[redacted:<kind>]` tokens.
 * Ordinary text is left untouched. Never throws.
 */
export function redactString(input: string): string {
  if (typeof input !== "string" || input.length === 0) return input;
  try {
    let out = input;

    // JWTs first — they are the most specific shape.
    out = out.replace(JWT_RE, "[redacted:jwt]");

    // Authorization: Bearer <token>.
    out = out.replace(BEARER_RE, "Bearer [redacted:token]");

    // Our session cookie.
    out = out.replace(SESS_COOKIE_RE, "avatar_sess=[redacted:cookie]");

    // Sensitive query-style params (preserve the key name).
    out = out.replace(QUERY_PARAM_RE, (_m, key: string) => `${key}=[redacted]`);

    // Long, mixed base64-ish blobs that survived the more specific passes.
    out = out.replace(LONG_BLOB_RE, (m) =>
      looksLikeKey(m) ? "[redacted:key]" : m,
    );

    return out;
  } catch {
    return input;
  }
}

/**
 * Strip the VALUES of sensitive query parameters from a URL, replacing them
 * with the literal `redacted` while preserving everything else (scheme, host,
 * path, non-sensitive params, hash). Falls back to `redactString` on any
 * malformed URL. Never throws.
 */
export function redactUrl(url: string): string {
  if (typeof url !== "string" || url.length === 0) return url;
  try {
    // `URL` requires an absolute URL; try as-is, then with a dummy base so we
    // can also handle relative URLs like `/path?token=x`.
    let parsed: URL;
    let relative = false;
    try {
      parsed = new URL(url);
    } catch {
      parsed = new URL(url, "http://redacted.invalid");
      relative = true;
    }

    let changed = false;
    for (const [name] of parsed.searchParams) {
      if (SENSITIVE_URL_PARAMS.includes(name.toLowerCase())) {
        parsed.searchParams.set(name, "redacted");
        changed = true;
      }
    }
    if (!changed) {
      // Nothing sensitive in the query, but the rest of the URL may still
      // carry secrets (e.g. an embedded JWT in the path).
      return redactString(url);
    }

    if (relative) {
      // Reconstruct without the synthetic origin.
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch {
    return redactString(url);
  }
}

/**
 * Deep-clone `obj`, replacing the values of sensitively-named keys with
 * `[redacted:key]` and running `redactString` over every remaining string
 * leaf. Arrays are handled. Cyclic / non-serializable inputs are tolerated
 * (best effort) and never cause a throw.
 */
export function redactObject<T>(obj: T): T {
  const seen = new WeakSet<object>();

  function walk(value: unknown): unknown {
    if (typeof value === "string") return redactString(value);
    if (value === null || typeof value !== "object") return value;

    // Cycle guard.
    if (seen.has(value as object)) return "[redacted:cycle]";
    seen.add(value as object);

    try {
      if (Array.isArray(value)) {
        return value.map((v) => walk(v));
      }

      // Plain objects (and anything object-like). For exotic objects we still
      // copy enumerable own keys; unreadable ones are skipped silently.
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(value as Record<string, unknown>)) {
        let child: unknown;
        try {
          child = (value as Record<string, unknown>)[key];
        } catch {
          continue;
        }
        if (SENSITIVE_KEY_SET.has(key.toLowerCase())) {
          out[key] = "[redacted:key]";
        } else {
          out[key] = walk(child);
        }
      }
      return out;
    } catch {
      return "[redacted:unserializable]";
    } finally {
      seen.delete(value as object);
    }
  }

  try {
    return walk(obj) as T;
  } catch {
    return obj;
  }
}
